import { Draft, Payload, State } from '@smile-next/uikit';
import { AutoLoginError, NotAuthorizedError } from '@/errors';
import { AuthParams } from '@/models/domain/AuthParams';
import { LoginId } from '@/models/domain/LoginId';
import { Password } from '@/models/domain/Password';
import { ConfirmedCloudIdPayload } from '@/usecases/ConfirmCloudIdUseCase';
import { InitializedNextAccountLoginFormPayload } from '@/usecases/InitializeNextAccountLoginFormUseCase';
import { AuthenticatedFailurePayload } from '@/usecases/payload/AuthenticatedFailurePayload';
import { AuthenticatedSuccessPayload } from '@/usecases/payload/AuthenticatedSuccessPayload';
import { UpdatedNextAccountLoginFormPayload } from '@/usecases/payload/UpdatedNextAccountLoginFormPayload';

export type NextAccountLoginPageStateProps = {
    authParams?: AuthParams;
    loginId?: LoginId;
    password?: Password;
};

export class NextAccountLoginPageState extends State {
    public readonly errorMessage?: string;
    public readonly shouldShowCloudId?: boolean;
    public readonly isAuthenticated?: boolean;
    public readonly shouldShowConfirmCloudId?: boolean;

    constructor(
        public readonly authParams?: AuthParams,
        public readonly loginId?: LoginId,
        public readonly password?: Password,
    ) {
        super();
        this.shouldShowCloudId = false;
        this.shouldShowConfirmCloudId = false;
        this.errorMessage = '';
    }

    reduce(this: Draft<this>, payload: Payload) {
        if (payload instanceof InitializedNextAccountLoginFormPayload) {
            const { authParams, loginId, password } = payload.initializeNextAccountPageStateProps;
            this.authParams = authParams;
            this.shouldShowCloudId = !authParams?.hasCloudId();
            this.loginId = loginId;
            this.password = password;
        }

        if (payload instanceof UpdatedNextAccountLoginFormPayload) {
            const { authParams, loginId, password } = payload.updateNextAccountPageStateProps;
            this.authParams = authParams;
            this.loginId = loginId;
            this.password = password;
            this.shouldShowCloudId = false;
            // update時は認証が完了しているのでtrueにする
            this.isAuthenticated = true;
            this.shouldShowConfirmCloudId = false;
        }

        if (payload instanceof AuthenticatedSuccessPayload) {
            this.isAuthenticated = true;
            this.shouldShowConfirmCloudId = false;
        }

        if (payload instanceof AuthenticatedFailurePayload) {
            const { error, isDisplayedCloudId } = payload.authenticatedFailurePayloadArgs;

            if (!(error instanceof AutoLoginError)) {
                // 自動ログイン失敗のエラーメッセージはユーザーを混乱させるため表示しない
                this.errorMessage = payload.authenticatedFailurePayloadArgs.error.message;
            }

            // バリデーションエラーですでに表示済みの場合やユーザー認証エラーでクラウドIDが表示済みの場合は表示状態を保持する
            this.shouldShowCloudId =
                isDisplayedCloudId || (error instanceof NotAuthorizedError && this.shouldShowCloudId);

            // 認証エラーかつクラウドIDが表示状態では無い時に表示する
            this.shouldShowConfirmCloudId = error instanceof NotAuthorizedError && !this.shouldShowCloudId;
            this.isAuthenticated = false;
        }

        if (payload instanceof ConfirmedCloudIdPayload) {
            this.shouldShowCloudId = true;
            this.shouldShowConfirmCloudId = false;
        }
    }

    get isValid() {
        return this.authParams?.cloudId?.isValid && this.loginId?.isValid && this.password?.isValid;
    }
}
